import { useId, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent, ReactNode } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useTranslation } from '@/i18n';
import type { Key } from '@/i18n';
import { ApiError } from '@/lib/api/types';
import type { DataError } from '@/lib/api/types';
import { cx } from '@/lib/cx';
import type { FlightAccessCard } from '@/types/flightAccess';
import { isUserActive, USER_ROLES } from '@/types/user';
import type {
  CompanyInfo,
  UserEditCard,
  UserInfo,
  UserRegisterCard,
  UserRole,
} from '@/types/user';
import { createUser, editUser, saveFlightAccess, setUserActivity } from './api';
import type { UserWriteResult } from './api';
import {
  ACCESS_FIELD_OF,
  accessProblems,
  cardOfDraft,
  draftOfAccess,
  errorsAfterChange,
  FlightAccessEditor,
} from './FlightAccessEditor';
import type { AccessDraft, AccessErrors, AccessField } from './FlightAccessEditor';
import { FlightAccessSection } from './FlightAccessSection';
import type { AccessBinding, AccessCarry } from './FlightAccessSection';
import {
  attachErrors,
  contactProblems,
  PasswordReveal,
  Readout,
  maskPhone,
  sanitizeTin,
  TIN_LENGTH,
} from './form';
import styles from './UserPanel.module.css';

/**
 * One account, in a dialog over the list — created or edited.
 *
 * Two rules from the server shape this form, and neither is a suggestion:
 *
 * - **An administrator cannot take the ADMIN role from themselves, and cannot
 *   disable their own account.** Roles are read from the database on every request,
 *   so either would take effect on the next call with no way back in through this
 *   API. The controls say so rather than being quietly absent — an administrator who
 *   wants this done needs to know it takes a second administrator.
 * - **`username` is set once.** It is the identity the token is issued against, and
 *   no edit endpoint has a field for it. Shown, never editable.
 *
 * The edit is **partial**: only the fields that actually changed are sent. Posting
 * the whole object back is accepted by the server, but it turns every save into a
 * write of every column — and it makes `role` present on every request, which is the
 * behaviour the API deliberately moved away from.
 *
 * This is the ADMIN form. The profile form behind `POST /user/me` is a different
 * shape on purpose — no role, no activity, and a password change that requires the
 * current one — so it belongs in its own component rather than behind a flag here.
 */

interface UserPanelProps {
  /** The account being edited, or null for the create form. */
  user: UserInfo | null;
  /** Companies to choose from when creating. */
  companies: readonly CompanyInfo[];
  /** The company to offer first — the signed-in administrator's own. */
  defaultCompanyId: number | null;
  /** The signed-in administrator, for the two "not to yourself" rules. */
  selfId: number | null;
  /**
   * The account's flight access. Edited in its own section when editing; when
   * creating, only `onSaved` is used — the access is written right after the account.
   */
  flightAccess: AccessBinding;
  /** `carry` only with `createdWithoutAccess`: what was chosen, and why it was refused. */
  onSaved: (user: UserInfo, action: SaveAction, carry?: AccessCarry) => void;
  onClose: () => void;
}

/**
 * `createdWithoutAccess` is a create whose SECOND write failed: the account exists and,
 * until its access is saved, sees every flight. Its own action because the screen has
 * to say so — as a warning, not as the success a plain `created` is.
 */
export type SaveAction = 'created' | 'createdWithoutAccess' | 'saved' | 'blocked' | 'unblocked';

/** The form's fields, all as strings — an input has no other kind of value. */
interface Draft {
  username: string;
  password: string;
  firstName: string;
  middleName: string;
  lastName: string;
  role: UserRole | '';
  phoneNumber: string;
  email: string;
  addressLine: string;
  tin: string;
  companyId: string;
}

const text = (value: string | null | undefined): string => value ?? '';

const draftFrom = (user: UserInfo | null, defaultCompanyId: number | null): Draft => ({
  username: text(user?.username),
  // Never populated from the server: the hash is not returned, and an input
  // pre-filled with one would be sent straight back as a new password.
  password: '',
  firstName: text(user?.firstName),
  middleName: text(user?.middleName),
  lastName: text(user?.lastName),
  role: user?.role ?? '',
  phoneNumber: text(user?.userCard?.phoneNumber),
  email: text(user?.userCard?.email),
  addressLine: text(user?.userCard?.addressLine),
  // Read as `iin`, written as `tin`. The same number under two names.
  tin: text(user?.userCard?.iin),
  companyId:
    user?.companyInfo?.id !== undefined
      ? String(user.companyInfo?.id)
      : defaultCompanyId !== null
        ? String(defaultCompanyId)
        : '',
});

/**
 * Which input a rejected field belongs to.
 *
 * The server's `path` may be dotted (`userCard.email`), so only the last segment is
 * matched. `iin` and `tin` both land on the one input, because the request and the
 * response genuinely disagree about its name and either could come back.
 */
const FIELD_OF: Record<string, keyof Draft> = {
  username: 'username',
  password: 'password',
  firstName: 'firstName',
  middleName: 'middleName',
  lastName: 'lastName',
  role: 'role',
  phoneNumber: 'phoneNumber',
  email: 'email',
  addressLine: 'addressLine',
  tin: 'tin',
  iin: 'tin',
  companyId: 'companyId',
};

/** The server's minimum, checked here too so a short password costs no round trip. */
const MIN_PASSWORD = 6;

/**
 * The edit dialog's tabs, one per thing an administrator opens an account to do:
 * its details, which flights it sees, and whether it may sign in. Each is its own
 * write with its own button, so each gets its own tab rather than one long scroll.
 */
const PANEL_TABS = ['details', 'access', 'status'] as const;
type PanelTab = (typeof PANEL_TABS)[number];

const TAB_LABELS: Record<PanelTab, Key> = {
  details: 'users.panel.details',
  access: 'users.access.title',
  status: 'users.columns.status',
};

export const UserPanel = ({
  user,
  // `companies` is still in the props and still passed: the picker that read it is
  // commented out below, so a new account takes `defaultCompanyId` — the signed-in
  // administrator's own company. Re-adding it here is all that restoring the picker
  // needs.
  defaultCompanyId,
  selfId,
  flightAccess,
  onSaved,
  onClose,
}: UserPanelProps) => {
  const { t, formatDateTime } = useTranslation();

  const isCreate = user === null;
  const isSelf = user !== null && selfId !== null && user.id === selfId;

  // No sync effect: the panel is keyed by the selection in UsersPage, so choosing
  // another account remounts it and this initialiser runs again. An effect copying
  // the prop into state would be the same bug the schedule filters avoid — it sets
  // state during the commit, and it would overwrite what is being typed.
  const [draft, setDraft] = useState<Draft>(() => draftFrom(user, defaultCompanyId));
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof Draft, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /*
    The edit dialog's open tab. The other two stay mounted and are only hidden, so a
    half-made change survives a look at another tab. A create whose access write
    failed opens on the access, because that is what still has to be saved.
  */
  const [tab, setTab] = useState<PanelTab>(() => (flightAccess.carry ? 'access' : 'details'));
  const tabsId = useId();
  const bodyRef = useRef<HTMLDivElement>(null);

  /*
    The new account's flight access — the create form only. An existing account's
    access is its own section with its own Save, below the form; see
    FlightAccessSection for why the two are not one.
  */
  const [accessDraft, setAccessDraft] = useState<AccessDraft>(() => draftOfAccess(null));
  const [accessErrors, setAccessErrors] = useState<AccessErrors>({});

  /** An administrator always sees every flight, so a new one has no access to set. */
  const accessApplies = draft.role !== 'ADMIN';

  const changeAccess = (next: AccessDraft, field: AccessField) => {
    setAccessDraft(next);
    setAccessErrors((current) => errorsAfterChange(current, field, next, t));
    setFormError(null);
  };

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    // The message described the value that was rejected, and this is no longer that
    // value. Leaving it under a field the reader has just corrected reads as the
    // correction having been refused.
    setFieldErrors((current) => ({ ...current, [key]: undefined }));
    // Same reasoning one level up: both messages the form can carry — a rejection
    // and "nothing has changed" — are verdicts on a submission that no longer
    // describes what is on screen.
    setFormError(null);
  };

  /** Spreads a failed write over the inputs that caused it. */
  const applyErrors = (errors: readonly DataError[], message: string | null) => {
    // Anything that matched no field goes above the form rather than being dropped.
    const { attached, loose } = attachErrors(errors, FIELD_OF);

    setFieldErrors(attached);
    setFormError(
      loose.length > 0
        ? loose.join(' ')
        : (message ??
            // A rejection with neither a field nor a message — a username already
            // taken is the usual one. Saying nothing would look like the button
            // failed to work.
            (Object.keys(attached).length > 0 ? null : t('users.panel.saveFailed'))),
    );
  };

  /** Runs a write and reports it, so the three actions share one path. */
  const run = async (write: () => Promise<UserWriteResult>, action: SaveAction) => {
    setFieldErrors({});
    setFormError(null);
    setIsSubmitting(true);

    try {
      const result = await write();

      if (result.ok) {
        onSaved(result.user, action);

        // The password box is emptied on the way out. It is the one field whose
        // value is not echoed back, so leaving it filled would mean a second Save
        // silently posting the same password again — and the field would keep
        // offering to change something that has already been changed.
        setDraft((current) => ({ ...current, password: '' }));
        setShowPassword(false);
        return;
      }

      applyErrors(result.errors, result.message);
    } catch (caught) {
      // The request itself failed — the network, a proxy, a 500. Reporting it as a
      // rejected field would send the reader to correct something that is fine.
      const status = caught instanceof ApiError ? caught.status : 0;
      setFormError(status === 0 ? t('users.panel.networkError') : t('users.panel.serverError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Creates the account, then — when it is to be restricted — its flight access.
   *
   * Two requests, in this order, because the second needs the id the first returns.
   * Between them the account exists and sees every flight, which is why both happen
   * before the dialog lets go: the API guide's rule is that both are done before the
   * password is handed over.
   *
   * If the second fails, the first is not undone — there is no delete. The screen says
   * so as a warning and opens the new account with the selection, and the reason it
   * was refused, still in hand.
   */
  const create = async (companyId: number, card: UserRegisterCard, access: FlightAccessCard | null) => {
    setFieldErrors({});
    setAccessErrors({});
    setFormError(null);
    setIsSubmitting(true);

    try {
      const result = await createUser(companyId, card);

      if (!result.ok) {
        applyErrors(result.errors, result.message);
        return;
      }

      if (access === null) {
        onSaved(result.user, 'created');
        return;
      }

      let carry: AccessCarry;

      try {
        const saved = await saveFlightAccess(result.user.id, access);

        if (saved.ok) {
          // Into the list first, so the account opens with its access already shown.
          flightAccess.onSaved(saved.access);
          onSaved(result.user, 'created');
          return;
        }

        // `errorMessage` repeats the first field error, so it is only the message when
        // nothing could be put under a field.
        const { attached, loose } = attachErrors(saved.errors, ACCESS_FIELD_OF);
        carry = {
          draft: accessDraft,
          errors: attached,
          message:
            loose.length > 0
              ? loose.join(' ')
              : Object.keys(attached).length > 0
                ? null
                : (saved.message ?? t('users.access.saveFailed')),
        };
      } catch (caught) {
        const status = caught instanceof ApiError ? caught.status : 0;
        carry = {
          draft: accessDraft,
          errors: {},
          message: status === 0 ? t('users.panel.networkError') : t('users.panel.serverError'),
        };
      }

      onSaved(result.user, 'createdWithoutAccess', carry);
    } catch (caught) {
      const status = caught instanceof ApiError ? caught.status : 0;
      setFormError(status === 0 ? t('users.panel.networkError') : t('users.panel.serverError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Checked here only where the rule is known and exact — the password floor,
    // what cannot be empty, and the three contact formats (an email's shape, a
    // phone's digit count, an IIN's twelve digits). Everything else is the
    // server's to judge, and guessing at its rules would mean a form that refuses
    // something the API would have accepted.
    if (user === null) {
      const card = registerCard(draft);

      const problems: Partial<Record<keyof Draft, string>> = contactProblems(draft, t);
      if (card.username === '') problems.username = t('common.required');
      if (card.password.length < MIN_PASSWORD) {
        problems.password = t('users.panel.passwordTooShort', { count: MIN_PASSWORD });
      }
      if (draft.companyId === '') problems.companyId = t('common.required');

      const accessIssues = accessApplies ? accessProblems(accessDraft, t) : {};

      if (Object.keys(problems).length > 0 || Object.keys(accessIssues).length > 0) {
        setFieldErrors(problems);
        setAccessErrors(accessIssues);
        setFormError(null);
        return;
      }

      // Only a restriction is written. "All flights" is what a new account already
      // has, so a second request for it would record a change that is not one.
      const access = accessApplies && accessDraft.restricted ? cardOfDraft(accessDraft) : null;

      void create(Number(draft.companyId), card, access);
      return;
    }

    const problems = contactProblems(draft, t);
    if (Object.keys(problems).length > 0) {
      setFieldErrors(problems);
      setFormError(null);
      return;
    }

    const changes = editCard(draft, user);

    // An empty body is a valid request that changes nothing, so it is not sent: a
    // save that writes nothing should say so rather than looking like it worked.
    if (Object.keys(changes).length === 0) {
      setFormError(t('users.panel.nothingChanged'));
      return;
    }

    void run(() => editUser(user.id, changes), 'saved');
  };

  const toggleActivity = () => {
    if (!user) return;

    const next = !isUserActive(user);
    void run(() => setUserActivity(user.id, next), next ? 'unblocked' : 'blocked');
  };

  const active = user === null ? true : isUserActive(user);

  const choose = (next: PanelTab) => {
    setTab(next);
    // A message belongs to the tab it was given in. Carried over to another one, it
    // would read as being about that one.
    setFormError(null);
    bodyRef.current?.scrollTo({ top: 0 });
  };

  /** Left and right move between the tabs, as a tablist is expected to. */
  const handleTabKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;

    const next = PANEL_TABS[(PANEL_TABS.indexOf(tab) + step + PANEL_TABS.length) % PANEL_TABS.length];
    if (!next) return;

    event.preventDefault();
    choose(next);
    document.getElementById(`${tabsId}-tab-${next}`)?.focus();
  };

  const header = (
    <header className={styles.header}>
      <div className={styles.identity}>
        <span className={styles.name}>
          {isCreate ? t('users.panel.create') : user.username}
        </span>

        {!isCreate && (
          <div className={styles.badges}>
            <Badge tone={active ? 'success' : 'danger'}>
              {t(active ? 'users.states.active' : 'users.states.blocked')}
            </Badge>
            {user.role && <Badge tone={roleTone(user.role)}>{t(`users.roles.${user.role}`)}</Badge>}
            {/* {isCompanyHead(user) && <Badge tone="info">{t('users.panel.companyHead')}</Badge>} */}
            {isSelf && <Badge tone="accent">{t('users.panel.you')}</Badge>}
          </div>
        )}
      </div>

      <button type="button" className={styles.close} onClick={onClose} aria-label={t('common.close')}>
        ×
      </button>
    </header>
  );

  const form = (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      {formError && (
        // Announced when it appears: a reader whose focus is still in the field
        // that caused it would otherwise never learn why nothing happened.
        <p className={styles.error} role="alert">
          {formError}
        </p>
      )}

      <Section title={t('users.panel.account')}>
        {isCreate ? (
          <>
            <Field
              label={t('users.panel.username')}
              value={draft.username}
              onChange={(event) => set('username', event.target.value)}
              {...(fieldErrors.username ? { error: fieldErrors.username } : {})}
              autoComplete="off"
              spellCheck={false}
              required
              disabled={isSubmitting}
            />

            <Field
              label={t('users.panel.password')}
              type={showPassword ? 'text' : 'password'}
              value={draft.password}
              onChange={(event) => set('password', event.target.value)}
              hint={t('users.panel.passwordHint', { count: MIN_PASSWORD })}
              {...(fieldErrors.password ? { error: fieldErrors.password } : {})}
              autoComplete="new-password"
              required
              disabled={isSubmitting}
              trailing={
                <PasswordReveal shown={showPassword} onToggle={() => setShowPassword((on) => !on)} />
              }
            />

            {/* <Labelled
              label={t('users.panel.company')}
              {...(fieldErrors.companyId ? { error: fieldErrors.companyId } : {})}
              hint={
                companies.length === 0 ? t('users.panel.noCompanies') : t('users.panel.companyHint')
              }
              required
            >
              <Select
                size="md"
                options={companies.map((company) => ({
                  value: String(company.id),
                  label: companyLabel(company),
                }))}
                placeholder={t('users.panel.chooseCompany')}
                value={draft.companyId}
                onChange={(event) => set('companyId', event.target.value)}
                disabled={isSubmitting || companies.length === 0}
                aria-label={t('users.panel.company')}
              />
            </Labelled> */}
          </>
        ) : (
          <>
            {/* Stated, not offered: there is no field for it on any edit
                endpoint, so an input here would be a control that cannot work. */}
            <Readout label={t('users.panel.username')} value={user.username} mono />

            {user.companyInfo && (
              <Readout label={t('users.panel.company')} value={companyLabel(user.companyInfo)} />
            )}

            <Field
              label={t('users.panel.newPassword')}
              type={showPassword ? 'text' : 'password'}
              value={draft.password}
              onChange={(event) => set('password', event.target.value)}
              {...(fieldErrors.password ? { error: fieldErrors.password } : {})}
              autoComplete="new-password"
              placeholder="••••••"
              disabled={isSubmitting}
              trailing={
                <PasswordReveal shown={showPassword} onToggle={() => setShowPassword((on) => !on)} />
              }
            />
          </>
        )}

        <Labelled
          label={t('users.panel.role')}
          {...(fieldErrors.role ? { error: fieldErrors.role } : {})}
          // The rule is stated where it applies. An administrator who needs their
          // own role changed has to know it takes a second administrator, which a
          // disabled control with no explanation does not say.
          // {...(isSelf ? { hint: t('users.panel.roleSelf') } : {})}
        >
          <Select
            size="md"
            options={USER_ROLES.map((role) => ({ value: role, label: t(`users.roles.${role}`) }))}
            // The empty option disappears the moment the account HAS a role:
            // `UserEditCard` has no way to say "remove the role", so offering
            // that choice would be a control that does nothing on save. It stays
            // for a new user, where the role is genuinely optional, and for an
            // existing one that has none — there it is the current state rather
            // than an option.
            {...(user?.role ? {} : { placeholder: t('users.panel.noRole') })}
            value={draft.role}
            onChange={(event) => set('role', event.target.value as UserRole | '')}
            disabled={isSubmitting || isSelf}
            aria-label={t('users.panel.role')}
          />
        </Labelled>
      </Section>

      <Section title={t('users.panel.person')}>
        <Field
          label={t('users.panel.lastName')}
          value={draft.lastName}
          onChange={(event) => set('lastName', event.target.value)}
          {...(fieldErrors.lastName ? { error: fieldErrors.lastName } : {})}
          autoComplete="off"
          disabled={isSubmitting}
        />
        <Field
          label={t('users.panel.firstName')}
          value={draft.firstName}
          onChange={(event) => set('firstName', event.target.value)}
          {...(fieldErrors.firstName ? { error: fieldErrors.firstName } : {})}
          autoComplete="off"
          disabled={isSubmitting}
        />
        <Field
          label={t('users.panel.middleName')}
          value={draft.middleName}
          onChange={(event) => set('middleName', event.target.value)}
          {...(fieldErrors.middleName ? { error: fieldErrors.middleName } : {})}
          autoComplete="off"
          disabled={isSubmitting}
        />
      </Section>

      <Section title={t('users.panel.contact')}>
        <Field
          label={t('users.panel.email')}
          type="email"
          value={draft.email}
          onChange={(event) => set('email', event.target.value)}
          {...(fieldErrors.email ? { error: fieldErrors.email } : {})}
          autoComplete="off"
          spellCheck={false}
          disabled={isSubmitting}
        />
        {/* Masked on the way in: only phone characters get past the keystroke,
            so submit is left judging the digit count alone. */}
        <Field
          label={t('users.panel.phone')}
          type="tel"
          value={draft.phoneNumber}
          onChange={(event) => set('phoneNumber', maskPhone(event.target.value, draft.phoneNumber))}
          placeholder="+7 700 123 45 67"
          {...(fieldErrors.phoneNumber ? { error: fieldErrors.phoneNumber } : {})}
          autoComplete="off"
          disabled={isSubmitting}
        />
        {/* Digits only, capped at twelve — an IIN cannot be typed wrong here,
            only left unfinished, which is what the submit check catches. */}
        <Field
          label={t('users.panel.tin')}
          value={draft.tin}
          onChange={(event) => set('tin', sanitizeTin(event.target.value))}
          {...(fieldErrors.tin ? { error: fieldErrors.tin } : {})}
          autoComplete="off"
          inputMode="numeric"
          maxLength={TIN_LENGTH}
          disabled={isSubmitting}
        />
        <Field
          label={t('users.panel.address')}
          value={draft.addressLine}
          onChange={(event) => set('addressLine', event.target.value)}
          {...(fieldErrors.addressLine ? { error: fieldErrors.addressLine } : {})}
          autoComplete="off"
          disabled={isSubmitting}
        />
      </Section>

      {/* The create form only — an existing account has its own section below,
          with its own Save. Last, because it is the last thing decided about a new
          account, and it is written last: right after the account exists. */}
      {isCreate && (
        <Section title={t('users.access.title')}>
          {accessApplies ? (
            <div className={styles.field}>
              <FlightAccessEditor
                value={accessDraft}
                onChange={changeAccess}
                errors={accessErrors}
                disabled={isSubmitting}
              />
              {accessDraft.restricted && (
                <span className={styles.message}>{t('users.access.createNote')}</span>
              )}
            </div>
          ) : (
            // Said rather than hidden: an administrator who picked the role and
            // then looks for the access should learn why there is none to set.
            <span className={styles.message}>{t('users.access.adminNote')}</span>
          )}
        </Section>
      )}

      <div className={styles.actions}>
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting
            ? t('users.panel.saving')
            : t(isCreate ? 'users.panel.createAction' : 'common.save')}
        </Button>
        <Button onClick={onClose} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );

  if (isCreate) {
    // Wide: eleven fields, and the three name parts read as a row rather than as a
    // column of three.
    return (
      <Modal label={t('users.panel.create')} onClose={onClose} width="lg">
        {header}
        <div className={styles.body}>{form}</div>
      </Modal>
    );
  }

  /** A tab panel, labelled by its tab. Hidden rather than unmounted — see `tab`. */
  const pane = (id: PanelTab) => ({
    role: 'tabpanel' as const,
    id: `${tabsId}-panel-${id}`,
    'aria-labelledby': `${tabsId}-tab-${id}`,
    hidden: tab !== id,
  });

  return (
    <Modal label={t('users.panel.edit')} onClose={onClose} width="lg">
      {header}

      <div className={styles.tabs} role="tablist" aria-label={t('users.panel.edit')}>
        {PANEL_TABS.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`${tabsId}-tab-${id}`}
            aria-controls={`${tabsId}-panel-${id}`}
            aria-selected={tab === id}
            tabIndex={tab === id ? 0 : -1}
            className={cx(styles.tab, tab === id && styles.tabActive)}
            onClick={() => choose(id)}
            onKeyDown={handleTabKey}
          >
            {t(TAB_LABELS[id])}
          </button>
        ))}
      </div>

      <div ref={bodyRef} className={cx(styles.body, styles.tabbed)}>
        <div {...pane('details')}>{form}</div>

        <div {...pane('access')} className={styles.pane}>
          <FlightAccessSection user={user} binding={flightAccess} />
        </div>

        <div {...pane('status')} className={styles.pane}>
          <div className={styles.status}>
            {formError && (
              <p className={styles.error} role="alert">
                {formError}
              </p>
            )}

            {(user.created || user.updated) && (
              <dl className={styles.facts}>
                {user.created && (
                  <div className={styles.fact}>
                    <dt>{t('users.panel.created')}</dt>
                    <dd>{formatDateTime(user.created)}</dd>
                  </div>
                )}
                {user.updated && (
                  <div className={styles.fact}>
                    <dt>{t('users.panel.updated')}</dt>
                    <dd>{formatDateTime(user.updated)}</dd>
                  </div>
                )}
              </dl>
            )}

            {/*
              This is the delete of this resource — the row stays, because the user's card
              and everything they have done reference it, and a blocked account simply
              cannot sign in. Outside every form, so Enter in a text field cannot reach it.
            */}
            <section className={cx(styles.danger, !active && styles.dangerRestore)}>
              <div className={styles.dangerText}>
                <h3 className={styles.dangerTitle}>
                  {t(active ? 'users.panel.blockTitle' : 'users.panel.unblockTitle')}
                </h3>
                <p className={styles.dangerHint}>
                  {isSelf
                    ? t('users.panel.blockSelf')
                    : t(active ? 'users.panel.blockHint' : 'users.panel.unblockHint')}
                </p>
              </div>

              <Button
                variant={active ? 'danger' : 'secondary'}
                onClick={toggleActivity}
                disabled={isSubmitting || isSelf}
              >
                {t(active ? 'users.panel.block' : 'users.panel.unblock')}
              </Button>
            </section>
          </div>
        </div>
      </div>
    </Modal>
  );
};

/** The create body: every field the reader filled in, and nothing they did not. */
const registerCard = (draft: Draft): UserRegisterCard => {
  const card: UserRegisterCard = {
    username: draft.username.trim(),
    // Not trimmed: a space is a legal character in a password and trimming one out
    // would store something other than what was typed and shown.
    password: draft.password,
  };

  if (draft.firstName.trim()) card.firstName = draft.firstName.trim();
  if (draft.middleName.trim()) card.middleName = draft.middleName.trim();
  if (draft.lastName.trim()) card.lastName = draft.lastName.trim();
  if (draft.role !== '') card.role = draft.role;
  if (draft.phoneNumber.trim()) card.phoneNumber = draft.phoneNumber.trim();
  if (draft.email.trim()) card.email = draft.email.trim();
  if (draft.addressLine.trim()) card.addressLine = draft.addressLine.trim();
  if (draft.tin.trim()) card.tin = draft.tin.trim();

  return card;
};

/**
 * The edit body: only what actually changed.
 *
 * The endpoint applies exactly the fields it is sent, so this is a diff rather than
 * the whole object. Two consequences worth stating:
 *
 * - `role` is absent unless it was changed, which is the difference between "leave
 *   the role alone" and "set it to this". The API used to require it on every edit
 *   and ignore it; sending it every time describes that old behaviour.
 * - A field the reader emptied is sent as `""`, not omitted and not `null` — `null`
 *   is documented as leaving the value alone, so an empty string is the only way
 *   this API can be asked to clear one.
 */
const editCard = (draft: Draft, user: UserInfo): UserEditCard => {
  const card: UserEditCard = {};
  const before = draftFrom(user, null);

  const changed = (key: keyof Draft) => draft[key].trim() !== before[key].trim();

  if (changed('firstName')) card.firstName = draft.firstName.trim();
  if (changed('middleName')) card.middleName = draft.middleName.trim();
  if (changed('lastName')) card.lastName = draft.lastName.trim();
  if (changed('phoneNumber')) card.phoneNumber = draft.phoneNumber.trim();
  if (changed('email')) card.email = draft.email.trim();
  if (changed('addressLine')) card.addressLine = draft.addressLine.trim();
  if (changed('tin')) card.tin = draft.tin.trim();

  // Only when a role was actually chosen. Clearing the select back to "no role" is
  // not expressible here — the card has no way to say "remove the role" — so it is
  // treated as no change rather than silently sent as something else.
  if (draft.role !== '' && draft.role !== user.role) card.role = draft.role;

  // An empty box means "leave the password alone", which is the whole reason it is
  // never pre-filled: a populated one would be posted back as a new password on
  // every save.
  if (draft.password !== '') card.password = draft.password;

  return card;
};

const companyLabel = (company: CompanyInfo): string =>
  company.companyName ?? company.companyType ?? `#${company.id}`;

/** ADMIN is the one role worth spotting in a list at a glance. */
export const roleTone = (role: UserRole) =>
  role === 'ADMIN' ? ('accent' as const) : ('neutral' as const);

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className={styles.section}>
    <h3 className={styles.sectionTitle}>{title}</h3>
    <div className={styles.grid}>{children}</div>
  </section>
);

/**
 * A label, a control and its message — `Field`'s layout for a control that is not an
 * input. It is here rather than in `components/ui` because one screen uses it; the
 * moment a second does, it belongs there instead.
 */
const Labelled = ({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) => (
  <div className={styles.field}>
    <span className={styles.label}>
      {label}
      {required && (
        <span className={styles.required} aria-hidden="true">
          *
        </span>
      )}
    </span>
    {children}
    {(error ?? hint) && (
      <span className={cx(styles.message, error && styles.messageError)}>{error ?? hint}</span>
    )}
  </div>
);

